import Link from "next/link";

import { Container } from "@/src/components/ui/Container";
import { Button } from "@/src/components/ui/Button";
import { Logo } from "@/src/components/ui/Logo";
import { Heading, Text, Highlight } from "@/src/components/ui/Typography";
import { FooterLinkColumn } from "@/src/components/molecules/FooterLinkColumn";
import { IsoStamps } from "@/src/components/molecules/IsoStamps";
import { ClutchLogo } from "@/src/components/molecules/PartnerLogos/icons/ClutchLogo";
import { GoodFirmsLogo } from "@/src/components/molecules/PartnerLogos/icons/GoodFirmsLogo";
import { GoogleLogo } from "@/src/components/molecules/PartnerLogos/icons/GoogleLogo";
import { paths } from "@/src/routes/paths";
import type { MenuTree } from "@/src/lib/menus/menus";

const offices = [
  {
    name: "New Jersey",
    address: "6 Goldenrod Ave, Bayville, NJ 08721",
  },
  {
    name: "India",
    address:
      "1501 Westport, near Taj Hotel, Sindhubhavan Road, Ahmedabad, Gujarat 380059.",
  },
];

// The official Google Maps URL API for a search query — no place ID
// needed, works for any freeform address string.
// https://developers.google.com/maps/documentation/urls/get-started
function googleMapsUrl(address: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const socialLinks = [
  { label: "LinkedIn", href: paths.linkedinTecheniac },
  { label: "Instagram", href: paths.instagramTecheniac },
];

const industryLinks = [
  { label: "HealthTech", href: paths.industries.healthcareSoftwareDevelopment },
  { label: "FinTech", href: paths.industries.fintechAiSolutions },
  { label: "MarTech", href: paths.industries.martechAiSolutions },
];

// stars: rating out of 5, in 0.5 increments — placeholders below, swap for
// the actual current rating on each platform.
const partnerLogos = [
  {
    name: "Clutch",
    href: paths.clutchTecheniac,
    icon: ClutchLogo,
    hoverColor: "var(--color-brand-clutch)",
    reviews: "16 Reviews",
    stars: 5,
  },
  {
    name: "GoodFirms",
    href: paths.goodfirmsTecheniac,
    icon: GoodFirmsLogo,
    hoverColor: "var(--color-brand-goodfirms)",
    reviews: "04 Reviews",
    stars: 5,
  },
  {
    name: "Google",
    href: paths.googleTecheniac,
    icon: GoogleLogo,
    multiColor: true,
    // Placeholder — no real Google review count exists in the codebase yet.
    // Swap for the actual count.
    reviews: "19 Reviews",
    stars: 5,
  },
];

interface FooterProps {
  about: MenuTree;
  services: MenuTree;
  legal: MenuTree;
}

export function Footer({ about, services, legal }: FooterProps) {
  return (
    <footer>
      <div data-dark-section className="bg-neutral-700">
        <Container className="py-16 md:py-24">
          <div className="flex flex-col justify-between gap-12 xl:flex-row">
            <div>
              <Heading
                as="h2"
                size="displayLG"
                className="max-w-125 text-white"
              >
                Let Us Talk About What You Are <Highlight>Building</Highlight>
              </Heading>

              <div className="mt-8 lg:mt-25 ">
                <Button
                  href={paths.bookACall}
                  className="w-full lg:w-auto"
                  variant="secondary"
                >
                  Book a free strategy session
                </Button>
              </div>
            </div>

            <div className="flex flex-1 flex-col justify-between gap-12 xl:max-w-160">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                {offices.map((office) => (
                  <Link
                    key={office.name}
                    href={googleMapsUrl(office.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block w-fit"
                  >
                    <Text size="bodySMMedium" className="text-neutral-50">
                      {office.name}
                    </Text>

                    <Text
                      size="bodySM"
                      className="mt-2 text-neutral-400 transition-colors duration-300 group-hover:text-neutral-50"
                    >
                      {office.address}
                    </Text>
                  </Link>
                ))}
              </div>

              <IsoStamps className="xl:justify-end" />
            </div>
          </div>
        </Container>
      </div>

      <div className="bg-white">
        <Container className="pt-16 md:pt-24 pb-5">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-0">
            <div className="flex flex-col items-start gap-8 lg:col-span-4 lg:pr-12">
              <div className="flex flex-col gap-4">
                <Logo />
                <Text size="bodySM" className="max-w-xs text-neutral-600">
                  Engineering AI-powered products that scale with your business.
                </Text>
              </div>

              <div className="flex gap-6">
                {socialLinks.map((social) => (
                  <Link
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                  >
                    <Text as="span" size="labelLG" className="text-neutral-600">
                      {social.label}
                    </Text>

                    <span className="absolute left-0 -bottom-1 h-px w-full origin-left scale-x-0 bg-neutral-600 transition-transform duration-300 group-hover:scale-x-100" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-12 sm:grid-cols-[1fr_2fr] lg:col-span-8 lg:border-l lg:border-neutral-200 lg:pl-12">
              <div className="flex flex-col gap-12">
                <FooterLinkColumn title="Company" links={about.nodes} />
                <FooterLinkColumn title="Industries" links={industryLinks} />
              </div>
              <FooterLinkColumn title="Services" links={services.nodes} />
            </div>
          </div>

          <div className="mt-16 flex flex-col items-center justify-between gap-6 border-t border-neutral-200 pt-5 sm:flex-row sm:items-center">
            <Text size="bodySM" className="text-neutral-500 hidden sm:block">
              © {new Date().getFullYear()} TechEniac. All rights reserved.
            </Text>

            {/* <PartnerLogos items={partnerLogos} /> */}

            <div className="flex gap-6">
              {legal.nodes.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  target={item.target}
                  rel={item.rel}
                >
                  <Text
                    as="span"
                    size="bodySM"
                    className="text-neutral-600 transition-colors duration-300 hover:text-brand"
                  >
                    {item.label}
                  </Text>
                </Link>
              ))}
            </div>

            <Text size="bodySM" className="text-neutral-500 sm:hidden">
              © {new Date().getFullYear()} TechEniac. All rights reserved.
            </Text>
          </div>
        </Container>
      </div>
    </footer>
  );
}
